"use client";

import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { FloatingInput } from "@/components/ui/floating-input";
import { FloatingTextarea } from "@/components/ui/floating-textarea";
import { FloatingSelect, SelectItem } from "@/components/ui/floating-select";
import { BrandButton } from "@/components/ui/brand-button";
import { OtpVerificationModal } from "../otp/OtpVerificationModal";
import { RootState, useAppDispatch, useAppSelector } from "@/lib/redux/root";
import { updateFormField } from "@/lib/redux/slices/uploadform.slice";
import {
	bindingtype,
	papertypes,
	printoptions,
	purposeToPaperTypes,
} from "@/config/constants";
import useFormAuth from "@/lib/hooks/useFormAuth";

import { z } from "zod";
import { toast } from "sonner";
import { uploadFormSchema } from "@/config/uploadform.schema";
import { UploadDetailsFormState } from "@/lib/redux/types/form.types";
import { isOtpValidForSevenDays } from "@/lib/utils/common.utils";
import { QuoteOrderPayload } from "@/lib/redux/types/order.types";
import { orderQuoteRequested } from "@/lib/redux/slices/order.slice";
import { useParams } from "react-router-dom";
import { setProductName } from "@/lib/redux/slices/common.slice";
import { FloatingDatePicker } from "@/components/ui/floating-date";
import { getinfofromzip } from "@/lib/services/api.services";
import { logClientError } from "@/lib/services/client-logger.service";

export function UploadForm() {
	const dispatch = useAppDispatch();
	const formData = useAppSelector((state: RootState) => state.uploadForm);
	const storredUserData = useAppSelector(
		(state: RootState) => state.common.storredUserData
	);
	const params = useParams();
	const product = params.product as string;
	const { pwuserid, locationAddressId, applicationType } = useAppSelector(
		(state: RootState) => state.location.location
	);
	const { loading, otpVerified } = useAppSelector(
		(state: RootState) => state.auth
	);

	const { loading: orderLoading } = useAppSelector(
		(state: RootState) => state.order
	);
	const { inProgess, progress } = useAppSelector(
		(state: RootState) => state.upload
	);
	const { handleSendOtp } = useFormAuth();

	const [formErrors, setFormErrors] = useState<Record<string, string>>({});
	const [showDropdown, setShowDropdown] = useState(false);

	// State for the API dropdown
	const [locationOptions, setLocationOptions] = useState<any[]>([]);

	const handleChange = (field: string, value: any) => {
		dispatch(
			updateFormField({ field: field as keyof UploadDetailsFormState, value })
		);

		try {
			// Validate the specific field by parsing the entire form with the updated value
			uploadFormSchema.parse({ ...formData, [field]: value });
			setFormErrors((prev) => ({ ...prev, [field]: "" }));
		} catch (err: any) {
			if (err instanceof z.ZodError) {
				// Only set error for the current field being changed
				const fieldError = err.issues.find((issue) => issue.path[0] === field);
				if (fieldError) {
					setFormErrors((prev) => ({
						...prev,
						[field]: fieldError.message || "Invalid value",
					}));
				} else {
					// Clear error if the field is valid
					setFormErrors((prev) => ({ ...prev, [field]: "" }));
				}
			}
		}
	};

	const validateForm = () => {
		let isValid = true;
		const newErrors: Record<string, string> = {};

		// 1. Run Zod Schema Validation
		try {
			uploadFormSchema.parse(formData);
		} catch (err: any) {
			isValid = false;
			if (err instanceof z.ZodError) {
				err?.issues.forEach((e: any) => {
					if (e.path[0]) {
						newErrors[e.path[0] as string] = e.message;
					}
				});
			}
		}

		// 2. Run Conditional Validation for Billing Details
		if (!formData.willcall) {
			if (!formData.ZipCode || formData.ZipCode.trim() === "") {
				newErrors["ZipCode"] = "Zip Code is Required";
				isValid = false;
			}
			if (!formData.address1 || formData.address1.trim() === "") {
				newErrors["address1"] = "Address is Required";
				isValid = false;
			}
		}

		// Update the error state
		setFormErrors(newErrors);
		return isValid;
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (progress.UnSupportedFile > 0) {
			toast.warning("Please remove the unsupported files before proceeding.");
			return;
		}

		dispatch(setProductName(product));

		const isValid = validateForm();

		if (!isValid) {
			return;
		}

		// Check if OTP is already verified and still valid for 7 days
		const isOtpStillValid = isOtpValidForSevenDays(
			formData.email,
			formData.otpVerifiedEmail,
			formData.otpVerifiedAt
		);

		if (isOtpStillValid) {
			// OTP is valid, proceed with order
			const data: QuoteOrderPayload = {
				firstname: formData.fullName.split(" ")[0],
				lastname: formData.fullName.split(" ").slice(1).join(" "),
				pwaccountid: pwuserid,
				orderinstruction: formData.description,
				email: formData.email,
				phoneno: formData.phone,
				purpose: formData.purpose,
				orderduedate: formData.dueDate,
				pono: formData.jobReference,
				// ordertype: OrderType.PRINT_ORDER,
				jobname: formData?.jobReference,
				company: formData.company,
				receiverpwaccountid: pwuserid,
				receiveraccountaddressid: locationAddressId,
				timezoneoffset: -new Date().getTimezoneOffset() / 60,
				sourceapptype: applicationType
					? applicationType
					: product === "arc"
						? 2
						: 1,
				printsize: formData?.sizeprint || "",
				papertype: formData?.papertype,
				bindtype: formData?.binding,
				noofsets: formData?.noofSets || "1",
				arcaccountno: formData?.arcaccountno || "",
				willcall: !!formData.willcall,
				...(!formData.willcall && {
					address1: formData.address1,
					city: formData.city,
					CountryName: formData.CountryName || "",
					ZipCode: formData.ZipCode || "",
					StateName: formData.StateName || "",
					stateid: formData.stateid || "",
					countryid: formData?.countryid || "",
				}),
			};

			dispatch(orderQuoteRequested(data));
		} else {
			// OTP not verified or expired - send OTP and open modal
			await handleSendOtp();
		}
	};

	useEffect(() => {
		if (storredUserData) {
			Object.entries(storredUserData).forEach(([key, value]) => {
				if (key === "dueDate") return;
				dispatch(
					updateFormField({ field: key as keyof UploadDetailsFormState, value })
				);
			});
		}
	}, [storredUserData]);

	const handleZipLookup = async (zip: string) => {
		handleChange("ZipCode", zip);
		handleChange("city", "");
		handleChange("StateName", "");
		handleChange("CountryName", "");
		handleChange("stateid", "");
		handleChange("countryid", "");
		setLocationOptions([]);
		setShowDropdown(true);

		if (zip.length >= 5) {
			try {
				const response = await getinfofromzip({ zip });
				const data = response?.data?.data;

				console.log(data, "test one two");

				if (data) {
					// 1. Check if citylist exists and has items, otherwise use standard city
					const cities =
						data.citylist && data.citylist.length > 0
							? data.citylist
							: [data.city];

					// 2. Map through the cities array to create multiple options
					const options = cities.map((cityName: string) => ({
						city: cityName,
						state: data.state,
						country: data.county, // API uses 'county' for Country Name
						formatted_address: `${cityName}, ${data.state}, ${data.county}`,
						zipCode: data.zipcode,
						countryid: data.country_id,
						stateid: data.state_id,
					}));

					setLocationOptions(options);
				}
			} catch (error: any) {
				console.error("API lookup failed:", error);
				await logClientError({
					level: "error",
					event: "zip_information_failed",
					message: "zip_information_failed",
					error,
					context: {
						zipcode: formData?.ZipCode,
						statusCode: error?.response?.status,
					},
				});
			}
		}
	};

	const fullConcatenatedAddress = [
		formData.address1,
		formData.city,
		formData.StateName,
		formData.ZipCode,
	]
		.filter(Boolean)
		.join(", "); // .filter(Boolean) safely removes empty parts

	return (
		<Card className='border-none bg-white p-6 shadow-md'>
			<div className='mb-6'>
				<h2 className='mb-1 text-xl font-semibold text-gray-800'>
					Enter your details
				</h2>
				<p className='text-sm text-gray-500'>
					Please provide as much detail as possible. Thank you!
				</p>
			</div>

			<form className='space-y-5' onSubmit={handleSubmit}>
				{/* Email + Phone */}
				<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
					{/* Email */}
					<FloatingInput
						disabled={inProgess}
						type='email'
						label='Email *'
						value={formData.email}
						onChange={(e) => handleChange("email", e.target.value)}
						error={formErrors.email}
						tabIndex={1}
						maxLength={51}
						enterKeyHint='next'
					/>

					{/* Phone */}
					<FloatingInput
						disabled={inProgess}
						type='tel'
						label='Phone Number *'
						value={formData.phone}
						onChange={(e) => handleChange("phone", e.target.value)}
						error={formErrors.phone}
						tabIndex={2}
						enterKeyHint='next'
						maxLength={31}
					/>
				</div>

				{/* Name + Company */}
				<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
					{/* Full Name */}
					<FloatingInput
						disabled={inProgess}
						type='text'
						label='Full Name *'
						value={formData.fullName}
						onChange={(e) => handleChange("fullName", e.target.value)}
						error={formErrors.fullName}
						tabIndex={3}
						maxLength={101}
						enterKeyHint='next'
					/>

					{/* Company */}
					<FloatingInput
						disabled={inProgess}
						type='text'
						label='Company *'
						value={formData.company}
						onChange={(e) => handleChange("company", e.target.value)}
						error={formErrors.company}
						tabIndex={4}
						maxLength={101}
						enterKeyHint='next'
					/>
				</div>

				{/* Job Reference */}
				<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
					<FloatingInput
						disabled={inProgess}
						type='text'
						label='Job Reference / PO#'
						value={formData.jobReference}
						onChange={(e) => handleChange("jobReference", e.target.value)}
						error={formErrors.jobReference}
						maxLength={31}
						tabIndex={5}
						enterKeyHint='next'
					/>
					<FloatingInput
						disabled={inProgess}
						type='text'
						label='ARC Account number'
						value={formData.arcaccountno}
						onChange={(e) => handleChange("arcaccountno", e.target.value)}
						error={formErrors.arcaccountno}
						tabIndex={6}
						maxLength={51}
						enterKeyHint='next'
						
					/>
				</div>
				{/* Date + Purpose */}
				<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
					{/* Due Date */}

					<FloatingDatePicker
						disabled={inProgess}
						label='Due date and time*'
						value={formData.dueDate}
						onChange={(val) => handleChange("dueDate", val)}
						error={formErrors.dueDate}
						tabIndex={7}
					/>

					{/* Purpose */}
					<FloatingSelect
						disabled={inProgess}
						label='Purpose *'
						value={formData.purpose}
						onValueChange={(value) => {
							const selected = printoptions.find(
								(item: any) => item.value.toString() === value
							);
							handleChange("purpose", value);
							handleChange("purposeName", selected?.name || "");

							// Clear previous paper type
							handleChange("papertype", "");
							handleChange("papertypeName", "");

							// Auto-select if only one paper type for this purpose
							const allowed = purposeToPaperTypes[value];
							if (allowed?.length === 1) {
								const autoPaper = papertypes.find(
									(p: any) => p.value === allowed[0]
								);
								if (autoPaper) {
									handleChange("papertype", autoPaper.value);
									handleChange("papertypeName", autoPaper.name);
								}
							}
						}}
						error={formErrors.purpose}
						placeholder='Select File(s) purpose'
						tabIndex={8}
						options={printoptions.map((item: any) => ({
							value: item.value.toString(),
							label: item.name,
						}))}
					>
						{printoptions.map((item: any) => (
							<SelectItem key={item.key} value={item.value.toString()}>
								{item.name}
							</SelectItem>
						))}
					</FloatingSelect>
				</div>
				<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
					<FloatingSelect
						disabled={inProgess}
						label='Paper type'
						value={formData.papertype}
						onValueChange={(value) => {
							const selected = papertypes.find(
								(item: any) => item.value.toString() === value
							);

							handleChange("papertype", value);
							handleChange("papertypeName", selected?.name || "");
						}}
						error={formErrors.papertype}
						placeholder='Select paper type'
						tabIndex={9}
						options={papertypes.map((item: any) => ({
							value: item.value.toString(),
							label: item.name,
						}))}
					>
						{papertypes.map((item: any) => (
							<SelectItem key={item.key} value={item.value.toString()}>
								{item.name}
							</SelectItem>
						))}
					</FloatingSelect>
					<FloatingSelect
						disabled={inProgess}
						label='Select binding'
						value={formData.binding}
						onValueChange={(value) => {
							const selected = bindingtype.find(
								(item: any) => item.value.toString() === value
							);

							handleChange("binding", value);
							handleChange("bindingName", selected?.name || "");
						}}
						error={formErrors.bindingtype}
						placeholder='Select binding'
						tabIndex={10}
						options={bindingtype.map((item: any) => ({
							value: item.value.toString(),
							label: item.name,
						}))}
					>
						{bindingtype.map((item: any) => (
							<SelectItem key={item.key} value={item.value.toString()}>
								{item.name}
							</SelectItem>
						))}
					</FloatingSelect>
				</div>
				<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
					<FloatingInput
						disabled={inProgess}
						type='text'
						label='# of Sets'
						value={formData.noofSets}
						onChange={(e) => handleChange("noofSets", e.target.value)}
						error={formErrors.noofSets}
						tabIndex={11}
						maxLength={51}
						enterKeyHint='next'
					/>
					<FloatingInput
						disabled={inProgess}
						type='text'
						label='Size of prints'
						value={formData.sizeprint}
						onChange={(e) => handleChange("sizeprint", e.target.value)}
						error={formErrors.sizeprint}
						tabIndex={12}
						maxLength={51}
						enterKeyHint='next'
					/>
				</div>

				{/* Description */}
				<FloatingTextarea
					disabled={inProgess}
					label='Print instructions*'
					value={formData.description}
					onChange={(e) => handleChange("description", e.target.value)}
					rows={4}
					error={formErrors.description}
					tabIndex={13}
					maxLength={2001}
					enterKeyHint='done'
				/>

				{/* --- NEW WILL CALL & BILLING SECTION --- */}
				<div className='mt-2 rounded-xl border border-gray-200 bg-[#f9fafb] p-5'>
					{/* Checkbox Row */}
					<div className='flex items-center justify-between'>
						<label
							className={`relative flex cursor-pointer items-center gap-3 ${inProgess ? "opacity-50" : ""}`}
						>
							<input
								type='checkbox'
								className='sr-only'
								disabled={inProgess}
								checked={!!formData.willcall}
								onChange={(e) => handleChange("willcall", e.target.checked)}
							/>
							{/* Blue Square Checkbox */}
							<div
								className={`flex h-5 w-5 items-center justify-center rounded-[4px] border transition-colors ${formData.willcall ? "border-red-600 bg-red-600" : "border-gray-300 bg-white"}`}
							>
								<svg
									className={`h-3.5 w-3.5 text-white transition-opacity ${formData.willcall ? "opacity-100" : "opacity-0"}`}
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'
									strokeWidth='3'
								>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										d='M5 13l4 4L19 7'
									/>
								</svg>
							</div>
							<span className='text-sm text-[#333333]'>
								I will pick up the order from the ARC Print Center.
							</span>

							{/* WILL CALL CHIP (Inside label to sit perfectly next to text) */}
							<span
								aria-hidden={!formData.willcall}
								className={`inline-flex items-center justify-center rounded-xl bg-[#ffeceb] px-3.5 py-1.5 text-[11px] font-bold tracking-wide text-[#b91c1c] uppercase transition-opacity duration-200 ${
									formData.willcall ? "opacity-100" : "opacity-0"
								}`}
							>
								Will Call
							</span>
						</label>
					</div>

					{/* Expanded Billing Form (Only shows if Will Call is unchecked) */}
					{!formData.willcall && (
						<div className='mt-5 border-t border-gray-200 pt-5'>
							<h3 className='mb-4 text-base font-semibold text-gray-900'>
								Delivery details
							</h3>

							<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
								{/* 1. ZIP Code (First) with Dropdown */}
								{/* FIX: Added "relative w-full" to this specific div to contain the absolute dropdown */}
								<div className='relative flex w-full flex-col'>
									<FloatingInput
										disabled={inProgess}
										type='text'
										label='ZIP Code *'
										value={formData.ZipCode}
										maxLength={10}
										onChange={(e) => handleZipLookup(e.target.value)}
										error={formErrors.ZipCode}
									/>

									{showDropdown && locationOptions.length > 0 && (
										<div className='absolute top-[calc(100%+4px)] left-0 z-50 max-h-60 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg'>
											{locationOptions.map((loc, idx) => (
												<div
													key={idx}
													className='flex cursor-pointer items-start gap-3 border-b border-gray-100 p-4 transition-colors last:border-none hover:bg-gray-50'
													onClick={() => {
														handleChange("ZipCode", loc.zipCode);
														handleChange("city", loc.city);
														handleChange("StateName", loc.state);
														handleChange("CountryName", loc.country);
														handleChange("stateid", loc.stateid);
														handleChange("countryid", loc.countryid);

														setShowDropdown(false);
													}}
												>
													{/* Map Pin Icon */}
													<svg
														className='mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500'
														fill='none'
														viewBox='0 0 24 24'
														stroke='currentColor'
														strokeWidth='1.5'
													>
														<path
															strokeLinecap='round'
															strokeLinejoin='round'
															d='M15 10.5a3 3 0 11-6 0 3 3 0 016 0z'
														/>
														<path
															strokeLinecap='round'
															strokeLinejoin='round'
															d='M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z'
														/>
													</svg>

													{/* Location Text */}
													<div className='flex flex-col'>
														<span className='text-base font-medium text-gray-800'>
															{loc.zipCode} - {loc.city}
														</span>
														<span className='text-sm text-gray-500'>
															{loc?.formatted_address}
														</span>
													</div>
												</div>
											))}
										</div>
									)}
								</div>

								{/* 2. Street Address (Second) */}
								<div className='flex flex-col'>
									<FloatingInput
										disabled={inProgess}
										type='text'
										label='Address (House No, Building, Street, Area) *'
										value={formData.address1}
										onChange={(e) => handleChange("address1", e.target.value)}
										maxLength={201}
										error={formErrors.address1}
									/>
								</div>
							</div>

							{/* 3. Fully Concatenated Address Box */}
							{(formData.ZipCode || formData.address1) && (
								<div className='mt-4 flex items-center gap-3 rounded-lg border border-transparent bg-[#eff4fa] p-4 text-sm transition-all duration-300'>
									{/* Map Pin Icon */}
									<svg
										className='h-5 w-5 flex-shrink-0 text-[#6b7280]'
										fill='none'
										viewBox='0 0 24 24'
										stroke='currentColor'
										strokeWidth='2'
									>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.243-4.243a8 8 0 1111.314 0z'
										/>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
										/>
									</svg>

									<div className='flex flex-col'>
										<span className='text-[10px] font-bold tracking-wider text-[#6b7280] uppercase'>
											Full Address
										</span>
										<span className='font-medium text-[#111827]'>
											{fullConcatenatedAddress}
										</span>
									</div>
								</div>
							)}
						</div>
					)}
				</div>
				{/* -------------------------------------------------- */}
				<p className='mt-2 text-xs text-gray-600'>
					This website uses reCAPTCHA for security purposes and is governed by
					our&nbsp;
					<a
						href='https://www.e-arc.com/privacy-policy/'
						target='_blank'
						tabIndex={14}
						className='text-blue-600 hover:underline'
					>
						Privacy Policy
					</a>{" "}
					and&nbsp;
					<a
						href='https://www.e-arc.com/security/'
						target='_blank'
						tabIndex={15}
						className='text-blue-600 hover:underline'
					>
						Security Policy.
					</a>{" "}
				</p>

				{/* Submit */}
				<div className='flex justify-end'>
					<BrandButton
						type='submit'
						tabIndex={16}
						className={`rounded-full px-8 py-6 text-lg text-white transition-all duration-200 ${
							loading
								? "cursor-not-allowed bg-gray-400"
								: otpVerified && inProgess
									? "bg-green-600 hover:bg-green-700"
									: ""
						}`}
						style={
							loading || orderLoading
								? { backgroundColor: "#9ca3af" }
								: otpVerified && inProgess
									? { backgroundColor: "#16a34a" }
									: undefined
						}
						disabled={loading || (otpVerified && inProgess)}
					>
						{loading
							? "Sending OTP..."
							: orderLoading
								? "Placing Order..."
								: otpVerified && inProgess
									? "Uploading..."
									: "Place order"}
					</BrandButton>
				</div>
			</form>

			<OtpVerificationModal />
		</Card>
	);
}

